import { Observable } from 'rxjs'
import { normalize, schema } from 'normalizr'

import { request, parseAPIError, DownloadRequest } from '../../common/api'
import { warehousingWarehouseSchema } from '../../common/normalizrSchemas'
import { normalizedEntitiesToRecordMap } from '../../common/helpers'

import * as actionTypes from './actionTypes'
import * as actionCreators from './actionCreators'
import * as tableModelActionTypes from '../../common/table/actionTypes'
import * as tableModelActionCreators from '../../common/table/actionCreators'
import { resolveModelState } from '../../common/table/helpers'
import { WarehousingWarehouse } from './model'

// Fetch

const fetchWarehousingWarehousesEpic = (action$, store) => {
    const tableIdentifier = 'warehousing_warehouses_list'
    const serverSide = true

    const triggeringAction = serverSide
        ? action$.ofType(actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED)
        : action$.ofType(actionTypes.FETCH)

    return triggeringAction
        .filter(action => action.type !== tableModelActionTypes.CONFIGURATION_CHANGED || action.payload.tableIdentifier === tableIdentifier)
        .switchMap(action => {
            const modelState = resolveModelState(tableIdentifier, store.getState(), action)

            const requestParams = {
                method: 'GET',
                path: 'warehousing-warehouses/list',
            }

            if (serverSide) {
                requestParams.method = 'POST'
                requestParams.path += `?page=${modelState.getIn(['pagination', 'current']) + 1}`
                requestParams.body = {
                    sorting: modelState.get('sorting').toJS(),
                    filters: modelState.get('filters').toJS(),
                }
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_STARTED,
                }),
                request(requestParams)
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(
                            serverSide ? ajaxResponse.response.data : ajaxResponse.response,
                            new schema.Array(warehousingWarehouseSchema)
                        )

                        const warehousingWarehouses = normalizedEntitiesToRecordMap(normalizedData.entities.warehousing_warehouses, WarehousingWarehouse, normalizedData.result)

                        const observables = [Observable.of(actionCreators.fetchWarehousingWarehousesFulfilled(warehousingWarehouses))]

                        serverSide &&
                            observables.push(
                                Observable.of(
                                    tableModelActionCreators.updatePagination(
                                        tableIdentifier,
                                        ajaxResponse.response.last_page,
                                        ajaxResponse.response.current_page - 1,
                                        ajaxResponse.response.total
                                    )
                                )
                            )

                        return Observable.concat(...observables)
                    })
                    .catch(error => Observable.of(actionCreators.fetchWarehousingWarehousesRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_CANCELLED, actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED))
            )
        })
}

// Fetch one

const fetchWarehousingWarehouseEpic = (action$, store) =>
    action$
        .ofType(actionTypes.FETCH_ONE)
        .filter(() => !store.getState().warehousingWarehouses.getIn(['current', 'fetching']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_ONE_STARTED,
                }),
                request({
                    path: `warehousing-warehouses/${action.payload}`,
                    method: 'GET',
                })
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(ajaxResponse.response, warehousingWarehouseSchema)

                        const warehousingWarehouses = normalizedEntitiesToRecordMap(normalizedData.entities.warehousing_warehouses, WarehousingWarehouse)

                        return Observable.concat(
                            Observable.of(actionCreators.fetchWarehousingWarehousesFulfilled(warehousingWarehouses)),
                            Observable.of(actionCreators.fetchWarehousingWarehouseFulfilled())
                        )
                    })
                    .catch(error => Observable.of(actionCreators.fetchWarehousingWarehousesRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_ONE_CANCELLED))
            )
        )

// Save

const saveWarehousingWarehouseEpic = (action$, store) =>
    action$
        .ofType(actionTypes.SAVE)
        .filter(() => !store.getState().warehousingWarehouses.getIn(['current', 'saving']))
        .switchMap(action => {
            const values = Object.assign({}, action.payload)

            let path = 'warehousing-warehouses'
            let method = 'POST'

            if (values.id) {
                path = `warehousing-warehouses/${values.id}`
                method = 'PUT'

                delete values.id
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.SAVE_STARTED,
                }),
                request({
                    path,
                    method,
                    body: values,
                })
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(ajaxResponse.response, warehousingWarehouseSchema)

                        const warehousingWarehouses = normalizedEntitiesToRecordMap(normalizedData.entities.warehousing_warehouses, WarehousingWarehouse)
                        const warehousingWarehouse = warehousingWarehouses.valueSeq().first()

                        return Observable.concat(Observable.of(actionCreators.saveWarehousingWarehouseFulfilled(warehousingWarehouse)))
                    })
                    .catch(error => Observable.of(actionCreators.saveWarehousingWarehouseRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.SAVE_CANCELLED))
            )
        })

// Delete

const deleteWarehousingWarehouseEpic = (action$, store) =>
    action$
        .ofType(actionTypes.DELETE)
        .filter(() => !store.getState().warehousingWarehouses.getIn(['deletion', 'inProgress']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.DELETE_STARTED,
                }),
                request({
                    path: `warehousing-warehouses/${action.payload}`,
                    method: 'DELETE',
                })
                    .switchMap(() => Observable.of(actionCreators.deleteWarehousingWarehouseFulfilled(action.payload)))
                    .catch(error => Observable.of(actionCreators.deleteWarehousingWarehouseRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.DELETE_CANCELLED))
            )
        )

// Export

const exportWarehousingWarehousesEpic = (action$, store) =>
    action$.ofType(actionTypes.EXPORT).switchMap(action => {
        const filters = JSON.stringify(action.payload.filters)
        const sorting = JSON.stringify(action.payload.sorting)
        const token = store.getState().auth.get('accessToken')

        new DownloadRequest({
            url: `warehousing-warehouses/export?filters=${filters}&sorting=${sorting}&token=${token}`,
        }).run()

        return Observable.concat(
            Observable.of({
                type: actionTypes.EXPORT_FULFILLED,
            })
        )
    })

export default [fetchWarehousingWarehousesEpic, fetchWarehousingWarehouseEpic, saveWarehousingWarehouseEpic, deleteWarehousingWarehouseEpic, exportWarehousingWarehousesEpic]
